import type { OrderLineRequest } from '@shared/api-responses';
import type { OrderItem } from '@shared/types';

export interface BreakdownLine {
  orderItemId: number;
  name: string;
  qty: number;
  unit: string | null;
}

export interface BreakdownRequest {
  requestId: number;
  submitterName: string | null;
  submittedAt: string;
  lines: BreakdownLine[];
}

export interface BreakdownStation {
  stationId: number;
  stationName: string;
  requests: BreakdownRequest[];
}

/**
 * Regroup an order's per-request split as station → request → lines, so a
 * consolidated order can be read back the way it was submitted.
 *
 * Only lines still on the order count: a removed line's allocation is history.
 * Quantities are what each request asked for, not what was bought.
 */
export function orderBreakdown(items: OrderItem[], split: OrderLineRequest[]): BreakdownStation[] {
  const lineById = new Map(items.map((i) => [i.id, i]));
  const stations = new Map<number, BreakdownStation>();

  for (const s of split) {
    const line = lineById.get(s.order_item_id);
    if (!line) continue;

    let station = stations.get(s.station_id);
    if (!station) {
      station = { stationId: s.station_id, stationName: s.station_name, requests: [] };
      stations.set(s.station_id, station);
    }
    let req = station.requests.find((r) => r.requestId === s.request_id);
    if (!req) {
      req = { requestId: s.request_id, submitterName: s.submitter_name, submittedAt: s.submitted_at, lines: [] };
      station.requests.push(req);
    }
    req.lines.push({ orderItemId: line.id, name: line.item_name, qty: s.qty, unit: line.unit });
  }

  return [...stations.values()]
    .sort((a, b) => a.stationId - b.stationId)
    .map((st) => ({ ...st, requests: st.requests.sort((a, b) => a.requestId - b.requestId) }));
}

/** "Oct 12, 3:04 PM" — enough to tell two requests from one station apart. */
export function formatSubmitted(iso: string): string {
  // D1's datetime('now') has no zone marker but is UTC.
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
